import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

const plans = [
  {
    name: 'Starter',
    price: '$20',
    period: '/mo',
    features: [
      '1 app build per month',
      'Basic research signals',
      'Standard build time',
      'Community support',
    ],
    isPopular: false,
  },
  {
    name: 'Pro',
    price: '$50',
    period: '/mo',
    features: [
      '3 app builds per month',
      'Full PRD & design brief',
      'Priority build queue',
      'Email support',
    ],
    isPopular: true,
  },
  {
    name: 'Business',
    price: '$100',
    period: '/mo',
    features: [
      '10 app builds per month',
      'Custom branding & export',
      'Dedicated support',
      'API access',
    ],
    isPopular: false,
  },
];

const PricingPage = () => {
  return (
    <div className="min-h-screen bg-white text-[#0F172A] pt-24 pb-16 px-6 relative overflow-hidden grid-bg">
      <div className="relative z-10 max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center space-y-4 mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#EFF6FF] text-[#2563EB] text-xs font-bold uppercase tracking-widest mb-4">
            Pricing
          </span>
          <h1 className="text-5xl sm:text-6xl font-extrabold text-[#0F172A] tracking-[-0.02em] leading-[1.2]">
            Simple, transparent pricing
          </h1>
          <p className="text-xl text-[#475569] leading-relaxed">
            Choose the plan that fits your needs
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              className={`relative rounded-2xl p-8 border ${
                plan.isPopular
                  ? 'bg-[#1E3A8A] border-[#1E3A8A] text-white grid-bg-dark'
                  : 'bg-white border-[#E2E8F0]'
              }`}
              style={{ boxShadow: plan.isPopular ? '0 20px 60px rgba(30, 58, 138, 0.3)' : '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -2px rgba(0,0,0,0.05)' }}
            >
              {plan.isPopular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <div
                    className="px-4 py-1.5 rounded-full bg-[#2563EB] text-white text-xs font-bold uppercase tracking-widest"
                    style={{ boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.39)' }}
                  >
                    Most Popular
                  </div>
                </div>
              )}

              <div className="text-center space-y-4 mb-8">
                <h3 className={`text-2xl font-bold ${plan.isPopular ? 'text-white' : 'text-[#0F172A]'}`}>{plan.name}</h3>
                <div className="flex items-baseline justify-center gap-1">
                  <span className={`text-5xl font-extrabold tracking-[-0.02em] ${plan.isPopular ? 'text-white' : 'text-[#0F172A]'}`}>
                    {plan.price}
                  </span>
                  <span className={plan.isPopular ? 'text-white/70' : 'text-[#475569]'}>{plan.period}</span>
                </div>
              </div>

              <ul className="space-y-4 mb-8">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${plan.isPopular ? 'bg-white/20' : 'bg-[#EFF6FF]'}`}>
                      <Check className={`w-3 h-3 ${plan.isPopular ? 'text-white' : 'text-[#2563EB]'}`} />
                    </div>
                    <span className={plan.isPopular ? 'text-white/90' : 'text-[#475569]'}>{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                className={`w-full py-3 rounded-lg font-semibold transition-all ${
                  plan.isPopular
                    ? 'bg-[#2563EB] hover:bg-[#1D4ED8] text-white'
                    : 'bg-transparent hover:bg-[#EFF6FF] text-[#2563EB] border-2 border-[#E2E8F0] hover:border-[#2563EB]'
                }`}
                style={plan.isPopular ? { boxShadow: '0 4px 14px 0 rgba(37, 99, 235, 0.39)' } : {}}
              >
                Get Started
              </button>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          className="mt-16 text-center text-[#475569] text-sm"
        >
          <p>All plans include access to the latest features and updates.</p>
          <p className="mt-2">Questions? Contact us at hello@1prompt.app</p>
        </motion.div>
      </div>
    </div>
  );
};

export default PricingPage;
